// frontend/src/components/DocumentViewer/DocumentViewer.tsx

import React, { useState, useCallback } from "react";
import api from "../../services/api";
import type { Document } from "../../services/api";
import FallbackPreview from "./FallbackPreview";
import PDFView from "./PDFView";
import ImageView from "./ImageView";
import FullscreenDocumentModal from "./FullscreenDocumentModal";

const API_BASE = api.defaults.baseURL ?? "/api";

interface DocumentViewerProps {
  document: Document;
  showControls?: boolean;
  containerClassName?: string;
  onError?: (error: Error) => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  showControls = true,
  containerClassName = "",
  onError,
}) => {
  const documentUrl = `${API_BASE}/documents/file/${document.id}`;

  const [error, setError] = useState<Error | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleError = useCallback(
    (err: Error) => {
      setError(err);
      onError?.(err);
    },
    [onError]
  );

  const handleFullscreen = () => {
    setIsFullscreen(true);
  };

  if (error || !documentUrl) {
    return (
      <div className={`flex flex-col w-full ${containerClassName}`}>
        <FallbackPreview
          type={document.type}
          message="Errore nel caricamento del documento"
        />
      </div>
    );
  }

  return (
    <div className={`flex flex-col w-full h-full ${containerClassName}`}>
      <div className="flex flex-col flex-1">
        {document.type === "pdf" ? (
          <PDFView
            url={documentUrl}
            onLoadError={handleError}
            onFullscreen={handleFullscreen}
            showControls={showControls}
          />
        ) : (
          <ImageView
            url={documentUrl}
            documentId={document.id}
            onFullscreen={handleFullscreen}
            showControls={showControls}
          />
        )}
      </div>

      <FullscreenDocumentModal
        isOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        document={document}
        documentUrl={documentUrl}
      />
    </div>
  );
};

export default DocumentViewer;
